import { create } from 'zustand';

interface PlayerState {
  currentId: string | null;
  isPlaying: boolean;
  progress: number;
  duration: number;
  audio: HTMLAudioElement | null;

  // Actions
  play: (id: string, audioUrl: string) => void;
  pause: () => void;
  stop: () => void;
  seek: (time: number) => void;
  setProgress: (progress: number) => void;
}

export const usePlayerStore = create<PlayerState>((set, get) => ({
  currentId: null,
  isPlaying: false,
  progress: 0,
  duration: 0,
  audio: null,

  play: (id, audioUrl) => {
    const { audio: existingAudio, currentId } = get();

    // If same audio, just resume
    if (currentId === id && existingAudio) {
      existingAudio.play();
      set({ isPlaying: true });
      return;
    }

    // Stop existing audio
    if (existingAudio) {
      existingAudio.pause();
      existingAudio.currentTime = 0;
    }

    // Create new audio element
    const audio = new Audio(audioUrl);

    audio.addEventListener('loadedmetadata', () => {
      set({ duration: audio.duration * 1000 });
    });

    audio.addEventListener('timeupdate', () => {
      set({ progress: audio.currentTime * 1000 });
    });

    audio.addEventListener('ended', () => {
      set({ isPlaying: false, progress: 0 });
    });

    audio.addEventListener('error', () => {
      console.error('Audio playback error');
      set({ isPlaying: false, currentId: null, audio: null });
    });

    audio.play();
    set({ audio, currentId: id, isPlaying: true, progress: 0 });
  },

  pause: () => {
    const { audio } = get();
    if (audio) {
      audio.pause();
      set({ isPlaying: false });
    }
  },

  stop: () => {
    const { audio } = get();
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    set({ isPlaying: false, progress: 0, currentId: null });
  },

  seek: (time) => {
    const { audio } = get();
    if (audio) {
      audio.currentTime = time / 1000;
      set({ progress: time });
    }
  },

  setProgress: (progress) => {
    set({ progress });
  },
}));
